import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Download, ArrowRight } from 'lucide-react';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate';
import FinancingInputCard from './FinancingInputCard';
import FinancingSummaryCards from './FinancingSummaryCards';
import AmortizationChart from './AmortizationChart';
import RepaymentScheduleTable from './RepaymentScheduleTable';
import LenderDetailsPanel from './LenderDetailsPanel';
import {
  calculateFinancing,
  getLenderAmortizationPeriods,
  getLenderLoanRange,
} from './financingCalculations';
import { exportFinancingPdf } from './financingPdfExport';

export default function FinancingCalculator({ lenders = [], loading, error }) {
  const [selectedLenderId, setSelectedLenderId] = useState(null);
  const [loanAmount, setLoanAmount] = useState(500000);
  const [amortizationMonths, setAmortizationMonths] = useState(60);
  const [calculation, setCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Auto-select first lender when data arrives
  useEffect(() => {
    if (lenders.length > 0 && !selectedLenderId) {
      setSelectedLenderId(String(lenders[0].id));
    }
  }, [lenders]);

  const selectedLender = lenders.find((l) => String(l.id) === String(selectedLenderId)) || null;
  const periods = getLenderAmortizationPeriods(selectedLender);

  // Debounced calculation
  useEffect(() => {
    if (!selectedLender) return;

    setIsCalculating(true);
    const timer = setTimeout(() => {
      const result = calculateFinancing(selectedLender, loanAmount, amortizationMonths);
      setCalculation(result);
      setIsCalculating(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedLender, loanAmount, amortizationMonths]);

  const handleLenderChange = (lenderId) => {
    setSelectedLenderId(lenderId);
    const newLender = lenders.find((l) => String(l.id) === String(lenderId));
    if (newLender) {
      const range = getLenderLoanRange(newLender);
      if (loanAmount < range.min) setLoanAmount(range.min);
      else if (loanAmount > range.max) setLoanAmount(range.max);

      // Reset amortization if outside new lender's range
      const newPeriods = getLenderAmortizationPeriods(newLender);
      if (!newPeriods.find((p) => p.months === amortizationMonths) && newPeriods.length > 0) {
        setAmortizationMonths(newPeriods[newPeriods.length - 1].months);
      }
    }
  };

  const handleExportPDF = () => {
    if (!selectedLender || !calculation) {
      window.alert('Please generate a financing calculation before exporting PDF.');
      return;
    }

    if (isExportingPdf) return;

    setIsExportingPdf(true);
    try {
      exportFinancingPdf({
        lender: selectedLender,
        loanAmount,
        amortizationMonths,
        calculation,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to export financing PDF', error);
      window.alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleUseCashFlow = () => {
    window.location.href = '/succession/calculators/cfa/start';
  };

  // Loading state
  if (loading) {
    return (
      <SingleColumnTemplate
        title="Financing Calculator"
        subtitle="Estimate your business acquisition loan repayment"
      >
        <div className="w-full space-y-8">
          <div className="space-y-2">
            <div className="h-8 bg-succession-bg-secondary rounded w-64 animate-pulse" />
            <div className="h-5 bg-succession-bg-secondary rounded w-96 animate-pulse" />
          </div>
          <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 h-40 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 h-32 animate-pulse"
              >
                <div className="h-4 bg-succession-bg-secondary rounded w-24 mb-4" />
                <div className="h-8 bg-succession-bg-secondary rounded w-32" />
              </div>
            ))}
          </div>
        </div>
      </SingleColumnTemplate>
    );
  }

  // Error state
  if (error) {
    return (
      <SingleColumnTemplate
        title="Financing Calculator"
        subtitle="Estimate your business acquisition loan repayment"
      >
        <div className="w-full min-h-[400px] flex items-center justify-center">
          <div className="text-center max-w-md space-y-4">
            <h2 className="text-2xl font-bold text-succession-text-primary">Error Loading Financing Data</h2>
            <p className="text-succession-text-secondary">
              We were unable to load the financing calculator. Please try refreshing the page.
            </p>
          </div>
        </div>
      </SingleColumnTemplate>
    );
  }

  // Empty state -- no lenders configured
  if (!lenders || lenders.length === 0) {
    return (
      <SingleColumnTemplate
        title="Financing Calculator"
        subtitle="Estimate your business acquisition loan repayment"
      >
        <div className="w-full min-h-[600px] flex items-center justify-center">
          <div className="text-center max-w-md space-y-4">
            <div className="w-24 h-24 mx-auto bg-succession-bg-secondary rounded-full flex items-center justify-center">
              <Download className="w-12 h-12 text-succession-text-secondary" />
            </div>
            <h2 className="text-2xl font-bold text-succession-text-primary">
              Financing Calculator Not Available
            </h2>
            <p className="text-succession-text-secondary">
              The financing calculator is not yet available for your organization. Please contact
              support for more information.
            </p>
          </div>
        </div>
      </SingleColumnTemplate>
    );
  }

  const pageActions = (
    <div className="flex items-center gap-3">
      <Button variant="outline" onClick={handleExportPDF} className="gap-2" disabled={isExportingPdf}>
        <Download className="w-4 h-4" />
        {isExportingPdf ? 'Exporting...' : 'Export PDF'}
      </Button>
      <Button variant="outline" onClick={handleUseCashFlow} className="gap-2">
        Use in Cash Flow Analysis
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
    <SingleColumnTemplate
      title="Financing Calculator"
      subtitle="Estimate your business acquisition loan repayment"
      actions={pageActions}
      variant="wide"
      columnStyle={{ maxWidth: '96rem' }}
    >
      <div className="w-full space-y-8">

      {/* Calculator Input Card */}
      <FinancingInputCard
        lenders={lenders}
        selectedLender={selectedLender}
        loanAmount={loanAmount}
        amortizationMonths={amortizationMonths}
        amortizationPeriods={periods}
        onLenderChange={handleLenderChange}
        onLoanAmountChange={setLoanAmount}
        onAmortizationChange={setAmortizationMonths}
      />

      {/* Results Summary Cards */}
      {calculation && !isCalculating && (
        <FinancingSummaryCards
          monthlyPayment={calculation.monthlyPayment}
          interestRate={calculation.interestRate}
          totalInterest={calculation.totalInterest}
          totalCost={calculation.totalCost}
        />
      )}

      {/* Loading skeleton */}
      {isCalculating && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 h-32 animate-pulse"
            >
              <div className="h-4 bg-succession-bg-secondary rounded w-24 mb-4" />
              <div className="h-8 bg-succession-bg-secondary rounded w-32" />
            </div>
          ))}
        </div>
      )}

      {/* Amortization Chart */}
      {calculation && !isCalculating && <AmortizationChart schedule={calculation.schedule} />}

      {/* Repayment Schedule Table */}
      {calculation && !isCalculating && (
        <RepaymentScheduleTable schedule={calculation.schedule} />
      )}

      {/* Lender Details Panel */}
      {selectedLender && <LenderDetailsPanel lender={selectedLender} />}

      {/* Action Bar */}
      <div className="sticky bottom-0 bg-succession-bg-primary/95 backdrop-blur-sm border-t border-succession-border p-6 rounded-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-succession-text-secondary">
            Ready to move forward with your financing?
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleExportPDF} className="gap-2" disabled={isExportingPdf}>
              <Download className="w-4 h-4" />
              {isExportingPdf ? 'Exporting...' : 'Export PDF'}
            </Button>
            <Button variant="outline" onClick={handleUseCashFlow} className="gap-2">
              Use in Cash Flow Analysis
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
    </SingleColumnTemplate>
  );
}
